/**
 * typed-field.component.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, ElementRef, Input, OnInit, Optional, Renderer, OnDestroy, ViewChild }
    from "@angular/core";
import { AbstractControl, FormArray, FormControl } from "@angular/forms";

import { inputless, oneline } from "../../support/utilities/functions";

import { DataType } from "./data-types";
import { TypedControlComponent } from "./typed-control.component";
import { TypedData, TypedDataHolderWithDefaults } from "./typed-data";
import { CollaborationService } from "app/intake-form/collaboration.service";
import { Collaborator } from "app/auth/identity/collaborator";
import { PingRequest } from "app/server/messages/general";
import { IntakeStepService } from "app/algorithms/management/intake-step.service";
import { IntakeStep } from "app/algorithms/structure";
import { Subscription } from "rxjs";

type FieldStatus = "empty" | "invalid" | "saved" | "received" | "answered" | "locked";

declare const require : (file : string) => string;

@Component({
    selector: "typed-field",
    template: require("./typed-field.template.html"),
    styles: [`
        .input-group {width: 100%;}
        .boolean input {
            display: none;
        }
        .input-group-btn, .input-group-btn button {
            height: 100%;
        }
        .number-type-icon {
            position: relative;
            display: inline-block;
            top: 1px;
            font-family: var(--title-font), inherit;
            font-size: 18px;
        }
        .number-type-icon::before {
            content: "#";
        }
        table {
            width: 100%;
        }
        tr, td {
            padding: 0px;
        }
        .input-group table:last-child .form-control
        {
            border-top-right-radius: 4px;
        }
        .input-group table:last-child tr:last-child td > *
        {
            border-bottom-right-radius: 4px;
        }
        .input-group table tr.lower-tray td > *
        {
            border-bottom: 1px solid #CCC;
        }
        .input-group table:last-child tr.lower-tray > *
        {
            border-right: 1px solid #CCC;
        }
        .btn.active {
            filter: brightness(0.8) saturate(0.5) contrast(2.5);
        }
    `]
})
export class TypedFieldComponent implements OnInit, OnDestroy
{
    /**
     * Maximum number of restriction options to display at the same time.
     * (In less abstract terms, the cutoff between a radio-style select box and
     * a traditional dropdown.)
     */
    // tslint:disable-next-line:no-unused-variable
    private restrictionDisplayLimit : number = 8; // TODO: config me

    @Input()
    public input : TypedDataHolderWithDefaults;

    @Input()
    public control : FormControl;

    @Input()
    public clearButton : boolean = true;
    public clearEnabled () : boolean
    {
        if (this.input.hints && this.input.hints.editable === false)
        {
            return false;
        }
        return this.clearButton;
    }

    @Input()
    public validationTray : boolean = true;

    public infoMessageHTML : string;

    @Input()
    public iconTray : boolean = true;

    @Input()
    public listActions : true | false | "remove" = false;

    @Input()
    public index : number = 0;

    @Input()
    public disableRestrictions : boolean = false;

    public deprecatedValues : any[] = [];

    private subscriptions : Subscription = new Subscription();

    constructor (
        @Optional() protected tcc : TypedControlComponent,
        protected renderer : Renderer,
        protected hostRef : ElementRef,
        protected collaboration : CollaborationService,
        protected intakes : IntakeStepService
    ) {
        if (tcc === null)
        {
            // this block is just for debugging inheritance problems;
            // if you're reading this, it probably can be removed
        }
    }

    public ngOnInit () : void
    {
        TypedData.applyDefaults(this.input);
        if (this.input.restrictions.length)
        {
            const val : any = this.input.value || this.control.value;
            if (this.input.cardinality === 1)
            {
                if (val !== null
                    && this.input.restrictions.indexOf(val) === -1)
                {
                    this.deprecatedValues = [val];
                }
            }
            else
            {
                if (Array.isArray(val))
                {
										this.deprecatedValues = val.filter(
                        (value : any, index : number) =>
														this.input.restrictions.indexOf(value) === -1
														&& this.input.value.indexOf(value) === index);
                }
                if (this.input.allowsDuplicates)
                {
                    // we don't want the selector to be as long when we
                    // potentially have multiple subfields in a row
                    this.restrictionDisplayLimit = 4;
                }
                else
                {
                    // present a special info message
                    this.infoMessageHTML = oneline`Hold <kbd>CTRL</kbd> or
                        <kbd>CMD</kbd> to select multiple.`;
                }
            }
        }
        this.setUpDifferenceCheck();
    }

    public ngOnDestroy () : void
    {
      this.subscriptions.unsubscribe();
    }

    @ViewChild('content') contentRef: ElementRef;
    public contentHeight : number;
    public ngAfterViewInit () : void
    {
        setTimeout(() => {
            // debugger;
            this.contentHeight = this.contentRef.nativeElement.offsetHeight;
        }, 250);
    }

    private setUpDifferenceCheck () : void
    {
      if (this.input.hints.highlightDifferenceWith)
      {
        this.subscriptions.add(this.control.valueChanges.subscribe(
          () => this.checkForDifferenceHighlight()));
        const comparison : IntakeStep =
          this.intakes.findNamed(this.input.hints.highlightDifferenceWith);
        this.subscriptions.add(comparison.control.valueChanges.subscribe(
          () => this.checkForDifferenceHighlight()));
        this.checkForDifferenceHighlight();
      }
    }
    private checkForDifferenceHighlight () : void
    {
      if (this.input.hints.highlightDifferenceWith)
      {
        const comparison : IntakeStep =
          this.intakes.findNamed(this.input.hints.highlightDifferenceWith);
        if (comparison.control.value !== this.control.value)
        {
          // debugger;
          this.infoMessageHTML = oneline`This answer may differ from the one in
              ${comparison.displayName} (${comparison.control.value}).`;
        }
        else
        {
          this.infoMessageHTML = "";
        }
      }
    }

    public integerlike (dt : DataType) : boolean
    {
        return ["integer", "whole number", "natural number"].indexOf(dt) > -1;
    }

    public static SUPPORTED_TYPES : DataType[] = [
        "boolean",
        "string",
        "date",
        "float",
        "integer",
        "whole number",
        "natural number",

        "json"
    ];

    public knownType (dt : DataType) : boolean
    {
        return TypedFieldComponent.SUPPORTED_TYPES.indexOf(dt) > -1;
    }

    public minimum (dt : DataType) : any
    {
        if (dt === "whole number")
        {
            return 0;
        }
        if (dt === "natural number")
        {
            return 1;
        }
        return null;
    }

    public static TYPE_ICONS : Map<DataType, string> = new Map([
        ["integer", "number-type-icon"],
        ["natural number", "number-type-icon"],
        ["whole number", "number-type-icon"],
        ["float", "number-type-icon"],
        ["string", "glyphicon glyphicon-font"],
        ["date", "glyphicon glyphicon-calendar"],
        ["boolean", "glyphicon glyphicon-question-sign"]
    ] as Array<[DataType, string]>);
    public static ERROR_ICONS : Map<string, string> = new Map([
        ["required", "glyphicon glyphicon-asterisk"],
        ["integer", "number-type-icon"],
        ["number", "number-type-icon"],
    ] as Array<[string, string]>);

    public getIcon () : string
    {
        // Validity icons
        if (this.control.invalid)
        {
            let chosen : string = "glyphicon glyphicon-exclamation-sign";
            TypedFieldComponent.ERROR_ICONS.forEach(
                (icon : string, error : string) => {
                    if (this.control.hasError(error))
                    {
                        chosen = icon;
                        return;
                    }
                });
            return chosen;
        }

        // Data source icons
        if (this.lockedByCollaborator())
        {
            return "glyphicon glyphicon-lock";
        }
        if (this.input.provider)
        {
            return "glyphicon glyphicon-user";
        }
        if (this.input.hints.autofill)
        {
            return "glyphicon glyphicon-paste";
        }

        // Behavior icons
        if (this.input.searchable)
        {
            return "glyphicon glyphicon-search";
        }
        if (this.input.hints.developer)
        {
            return "glyphicon glyphicon-wrench";
        }

        // Data type icons
        if (this.input.restrictions.length)
        {
            return (
                (this.input.cardinality === 1)
                ? "glyphicon glyphicon-hand-right"
                : "glyphicon glyphicon-list"
            );
        }
        if (this.input.hints.currency)
        {
            return "glyphicon glyphicon-usd";
        }
        return TypedFieldComponent.TYPE_ICONS.get(this.input.type);
    }

    private lockedByCollaborator () : boolean
    {
      const disabled : boolean = Number.isInteger(this.input.id) &&
        (this.collaboration.locks.has(this.input.id)
          || this.collaboration.tableLocks.has(this.input.hints.table));
      if (this.control.disabled !== disabled)
      {
        if (disabled)
        {
          this.control.disable();
        }
        else
        {
          this.control.enable();
        }
      }
      return disabled;
    }

    public status () : FieldStatus
    {
        if (this.lockedByCollaborator())
        {
          return "locked";
        }
        if (this.control.invalid)
        {
            return "invalid";
        }
        if (!this.control.dirty)
        {
            if (inputless(this.control.value))
            {
                // valid + clean + unvalued = empty
                return "empty";
            }
            // valid + clean + valued = saved
            return "saved";
        }
        if (this.input.provider)
        {
            // valid + dirty + provided = received
            return "received";
        }
        // valid + dirty + unprovided = answered
        return "answered";
    }

    public containerClass () : string
    {
        return (
            (this.validationTray || this.clearEnabled() || this.iconTray)
            ? this.status() + " input-group"
            : this.status()
        );
    }

    private getLockersName () : string
    {
      const locker : Collaborator | undefined =
        this.collaboration.findUserIdByLock(
          this.input.id, this.input.hints.table);
      if (locker === undefined)
      {
        return "«unknown person»";
      }
      return locker.name;
    }

    public tooltip () : string
    {
        switch (this.status())
        {
            case "invalid":
                return `This field is invalid.`;
            case "locked":
                return oneline`
                  This field is currently being edited by user
                  ${this.getLockersName()}`;
            case "empty":
                return (
                    this.input.searchable
                        ? `This field is used to index the record in the
                            database.You are strongly encouraged to enter a
                            value so that the record can be found via search.`
                        : `You can enter ${this.input.type}s in this field`
                );
            case "saved":
                return `This field has been saved.`;
            case "received":
                return oneline`This field was modified by
                  ${this.input.provider || "another user"}.`;
            case "answered":
                return `You entered a value since the last save.`;
        }
    }
    
    public issueChange () : void
    {
        // if (this.input.control instanceof FormArray)
        // {
        //    this.input.control.setValue(this.input.control.getRawValue());
        // }
        const e : Event =
            new Event("change", {bubbles: true, cancelable: true});
        (this.hostRef.nativeElement as HTMLInputElement).dispatchEvent(e);
        this.checkForDifferenceHighlight();
    }

    public move (delta : -1 | 1) : void
    {
        const arr : FormArray = this.input.control as FormArray;
        arr.removeAt(this.index);
        arr.insert((this.index + delta), this.control);
        this.issueChange();
    }

    public remove () : void
    {
        (this.input.control as FormArray).removeAt(this.index);
        this.issueChange();
    }

    public top () : boolean
    {
        return (
            !(this.input.control instanceof FormArray) || (this.index === 0)
        );
    }

    public bottom () : boolean
    {
        return (
            !(this.input.control instanceof FormArray) ||
            (this.index === (this.input.control.length - 1))
        );
    }

    public only () : boolean
    {
        return (
            !(this.input.control instanceof FormArray) ||
            (this.input.control.length === 1)
        );
    }

    public clear () : void
    {
        if (this.input.cardinality === 0 && !this.input.allowsDuplicates)
        {
            this.control.setValue([]);
        }
        else
        {
            this.control.setValue(null);
        }
        this.issueChange();
    }

    public boolean (value : any) : boolean
    {
        return typeof value === "boolean";
    }

    public adjustBoolean (shift : number) : void
    {
        console.log("adjusting by ", shift);
        const V : boolean[] = [true, false, null];
        const target : number = V.indexOf(this.control.value) + shift;
        if (target >= 0 && target < V.length)
        {
            this.control.setValue(V[target]);
            this.issueChange();
        }
    }

    public setBoolean (value : boolean) : void
    {
        this.control.setValue(value);
        this.issueChange();
    }

    public gainFocus () : void
    {
      if (this.input.id)
      {
        const table : string | null = this.input.hints.table;
        this.collaboration.sendStepLock(this.input.id, table);
      }
    }

    public loseFocus () : void
    {
      if (this.input.id)
      {
        this.collaboration.sendUnlock();
      }
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/common/data/typed-field.component.ts